import * as styles from "./DateRecurrencePanel.css";

import { clsx } from "clsx";
import { type JSX, useId } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { CopyButton } from "@frontend/components/atoms/CopyButton";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useLingui } from "@lingui/react/macro";

import { DateRecurrenceEditor } from "./DateRecurrenceEditor";
import { DateRecurrenceExceptions } from "./DateRecurrenceExceptions";
import type { DateRecurrencePanelProps } from "./DateRecurrencePicker.types";
import { DateRecurrenceTour } from "./DateRecurrenceTour";
import { recurrenceLines } from "./recurrenceLines";

export function DateRecurrencePanel({
  value,
  onValueChange,
  exceptions = [],
  onExceptionsChange,
  startDate,
  onStartDateChange,
  showEnd = false,
  heading,
  actions,
  tour = true,
  onTourRunningChange,
  onCopy,
  framed = true,
  scrollable = false,
  locale,
  className,
}: DateRecurrencePanelProps): JSX.Element {
  const { t } = useLingui();

  const id = useId();

  const scope = `[data-recurrence-panel="${id}"]`;

  const body = (
    <div className={styles.body}>
      <DateRecurrenceEditor
        value={value}
        locale={locale}
        startDate={startDate}
        onStartDateChange={onStartDateChange}
        showEnd={showEnd}
        onValueChange={(next) => onValueChange?.(next)}
      />

      {onExceptionsChange && (
        <DateRecurrenceExceptions
          exceptions={exceptions}
          locale={locale}
          onChange={onExceptionsChange}
        />
      )}
    </div>
  );

  return (
    <TooltipProvider>
      <div
        data-slot="date-recurrence-panel"
        data-recurrence-panel={id}
        className={clsx(styles.panel, framed && styles.panelFramed, className)}
      >
        <div className={styles.header}>
          {heading ?? <span className={styles.title}>{t`Recurrence`}</span>}

          <div className={styles.actions}>
            {tour && (
              <DateRecurrenceTour
                scope={scope}
                schedule={Boolean(onStartDateChange) || showEnd}
                exceptions={Boolean(onExceptionsChange)}
                onRunningChange={onTourRunningChange}
              />
            )}

            <Tooltip>
              <TooltipTrigger
                render={
                  <CopyButton
                    variant="ghost"
                    size="sm"
                    label={t`Copy the rule`}
                    copiedLabel={t`Rule copied`}
                    value={() =>
                      recurrenceLines({ value, startDate, exceptions }).join(
                        "\n",
                      )
                    }
                    onCopy={onCopy}
                  />
                }
              />

              <TooltipContent>{t`Copy the rule`}</TooltipContent>
            </Tooltip>

            {actions}

            {value && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`No recurrence`}
                      onClick={() => onValueChange?.("")}
                      leftSection={<Icon name="RotateCcw" size="sm" />}
                    />
                  }
                />

                <TooltipContent>{t`No recurrence`}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {scrollable ? (
          <ScrollArea className={styles.scrollArea}>
            <ScrollAreaViewport className={styles.viewport}>
              {body}
            </ScrollAreaViewport>

            <ScrollAreaScrollbar>
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>
        ) : (
          body
        )}
      </div>
    </TooltipProvider>
  );
}
